import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import type { OrderSheetLine, OrderSheetResponse } from '../../services/orderSheetService';

/**
 * PO-SHEET.1 — the order sheet. One line format, used by both doors.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE LAW: a suggestion is advice. It is never an order.
 *
 * `orderQty` starts EMPTY on every line and stays empty until a person types
 * a number or taps the suggestion. An untouched line does not exist on the PO
 * — it is not sent with a zero, it is not sent at all.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * SHOWING THE ARITHMETIC IS THE FEATURE. "9" is a number to be trusted or
 * distrusted with no way to tell which; "par 12 − 3 = 9" is a claim the reader
 * can check at a glance, and disagree with precisely — "par is wrong", not
 * "the app is wrong". It is also why nobody needs a finance vocabulary to use
 * this screen.
 */

export interface OrderSheetProps {
  sheet: OrderSheetResponse;
  loading?: boolean;
  /** Server-side search term, echoed so "accept all" can respect it. */
  query: string;
  onQueryChange: (q: string) => void;
  /** Lines the user has actually filled. Empty until they do. */
  quantities: Record<string, number>;
  onQuantitiesChange: (next: Record<string, number>) => void;
  onCreate: () => void;
  creating?: boolean;
}

const money = (minor: number, currency: string | null) => {
  const major = minor / 100;
  return `${currency === 'HKD' ? 'HK$' : (currency ?? '')}${major.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const OrderSheet: React.FC<OrderSheetProps> = ({
  sheet, loading = false, query, onQueryChange,
  quantities, onQuantitiesChange, onCreate, creating = false,
}) => {
  const theme = useTheme();
  const [acceptedAll, setAcceptedAll] = useState(false);

  const filled = useMemo(
    () => sheet.lines.filter(l => (quantities[l.inventoryItemId] ?? 0) > 0),
    [sheet.lines, quantities],
  );

  const totals = useMemo(() => {
    let bottles = 0;
    let value = 0;
    let unpriced = 0;
    for (const l of filled) {
      const qty = quantities[l.inventoryItemId] ?? 0;
      bottles += qty;
      if (l.lastCost != null) value += l.lastCost * qty;
      else unpriced += 1;
    }
    return { bottles, value, unpriced };
  }, [filled, quantities]);

  const setQty = (id: string, raw: string) => {
    const next = { ...quantities };
    const n = Math.max(0, Math.ceil(Number(raw)));
    if (!raw || !Number.isFinite(n) || n <= 0) delete next[id];
    else next[id] = n;
    onQuantitiesChange(next);
  };

  /** One tap = one line filled with its own suggestion. Never automatic. */
  const acceptOne = (line: OrderSheetLine) => {
    if (line.suggestion.qty == null) return;
    onQuantitiesChange({ ...quantities, [line.inventoryItemId]: line.suggestion.qty });
  };

  /**
   * §8 — "Accept all" is a deliberate act with a visible result, never a
   * default. It respects the CURRENT search filter and says how many lines
   * that is, so nobody accepts two hundred suggestions believing they accepted
   * six.
   */
  const acceptable = sheet.lines.filter(l => l.suggestion.qty != null);
  const acceptAll = () => {
    const next = { ...quantities };
    acceptable.forEach(l => { next[l.inventoryItemId] = l.suggestion.qty as number; });
    onQuantitiesChange(next);
    setAcceptedAll(true);
  };
  const clearAll = () => { onQuantitiesChange({}); setAcceptedAll(false); };

  return (
    <Box
      // PO-SHEET.1c — one identity both doors can be asserted against. The
      // previous "both doors land here" test rendered this component directly
      // and so could not tell whether a door actually reached it.
      data-testid="order-sheet"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {sheet.meta.unlinkedFallback && (
        <Alert severity="info">
          No items are linked to {sheet.supplier.name} yet, so this is your whole
          catalogue. Order what they supply — the link records itself when the
          delivery is received.
        </Alert>
      )}

      {sheet.meta.parCoverage === 0 && sheet.lines.length > 0 && (
        // §10 — the zero-par venue. Two hundred empty suggestion cells would be
        // theatre; one sentence explains the column and offers the fix.
        <Alert severity="info">
          No par levels are set, so there are no suggestions to offer yet. You can
          still order by typing quantities — and setting par levels on your items
          will fill this column in.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={`Search ${sheet.supplier.name ?? 'this supplier'}’s items`}
          value={query}
          onChange={e => onQueryChange(e.target.value)}
          inputProps={{ 'aria-label': 'Search this order sheet' }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Button
          size="small"
          onClick={acceptAll}
          disabled={acceptable.length === 0}
          sx={{ textTransform: 'none' }}
        >
          Accept {acceptable.length} shown
        </Button>
        {Object.keys(quantities).length > 0 && (
          <Button size="small" onClick={clearAll} sx={{ textTransform: 'none' }}>
            Clear
          </Button>
        )}
      </Box>

      {acceptedAll && filled.length > 0 && (
        <Alert severity="success" onClose={() => setAcceptedAll(false)}>
          Filled {filled.length} line{filled.length === 1 ? '' : 's'} · {totals.bottles} bottles
          {totals.value > 0 && <> · ≈ {money(totals.value, sheet.currency)}</>}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">On hand</TableCell>
              <TableCell align="right">Par</TableCell>
              <TableCell>Suggested</TableCell>
              <TableCell align="right">Order</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}
            {!loading && sheet.lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Nothing matches that search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && sheet.lines.map(line => {
              const qty = quantities[line.inventoryItemId];
              return (
                <TableRow key={line.inventoryItemId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{line.displayName}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}>
                      {line.sizeValue && (
                        <Typography variant="caption" color="text.secondary">
                          {line.sizeValue} {line.sizeUnit}
                        </Typography>
                      )}
                      {line.unlinked && (
                        <Chip label="unlinked" size="small" sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600 }} />
                      )}
                      {line.suggestion.needsCount && (
                        <Chip
                          label="needs count"
                          size="small"
                          sx={{
                            fontSize: '0.68rem', height: 20, fontWeight: 600, border: 'none',
                            color: theme.palette.warning.main,
                            backgroundColor: alpha(theme.palette.warning.main, 0.12),
                          }}
                        />
                      )}
                      {line.onOpenOrder && (
                        <Tooltip title={`${line.onOpenOrder.orderedQty} already on an open order for this supplier`}>
                          <Chip
                            label="on order"
                            size="small"
                            sx={{
                              fontSize: '0.68rem', height: 20, fontWeight: 600, border: 'none',
                              color: theme.palette.info.main,
                              backgroundColor: alpha(theme.palette.info.main, 0.12),
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {line.onHand}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {line.parLevel ?? '—'}
                  </TableCell>

                  <TableCell>
                    {line.suggestion.qty != null ? (
                      // Tappable, and it reads as its own arithmetic.
                      <Chip
                        label={line.suggestion.explain}
                        size="small"
                        clickable
                        onClick={() => acceptOne(line)}
                        aria-label={`Accept ${line.suggestion.qty} for ${line.displayName}`}
                        sx={{
                          fontSize: '0.68rem', height: 20, fontWeight: 600, border: 'none',
                          maxWidth: '100%',
                          color: theme.palette.text.secondary,
                          backgroundColor: alpha(theme.palette.text.primary, 0.06),
                          '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.12) },
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {line.suggestion.explain}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      value={qty ?? ''}
                      onChange={e => setQty(line.inventoryItemId, e.target.value)}
                      inputProps={{
                        min: 0,
                        'aria-label': `Order quantity for ${line.displayName}`,
                        style: { textAlign: 'right', padding: '4px 8px', width: 62 },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* §8 — the running footer. The ≈ is load-bearing: this is built from
          last known cost and is an estimate, never a quotation. */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          position: 'sticky', bottom: 0, zIndex: 2,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {filled.length} line{filled.length === 1 ? '' : 's'} · {totals.bottles} bottles
          {totals.value > 0 && <> · ≈ {money(totals.value, sheet.currency)}</>}
        </Typography>
        {totals.unpriced > 0 && (
          // A total that hides its own gaps is the kind of number someone
          // forwards to a supplier.
          <Typography variant="caption" color="text.secondary">
            {totals.unpriced} line{totals.unpriced === 1 ? ' has' : 's have'} no known cost
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          onClick={onCreate}
          disabled={creating || filled.length === 0}
          sx={{ textTransform: 'none' }}
        >
          {creating ? 'Creating…' : `Create draft PO (${filled.length})`}
        </Button>
      </Paper>
    </Box>
  );
};

export default OrderSheet;
